/**
 * InactivityGuard
 *
 * Monitors user activity (mouse, keyboard, touch, scroll) and signs the
 * user out after a configurable period of inactivity.
 *
 * - Only active when the user is authenticated.
 * - Resets the timer on any interaction event.
 * - Shows a warning dialog 60 seconds before timeout so the user can
 *   choose to stay signed in.
 * - On timeout, calls Clerk signOut and redirects to /login.
 *
 * Place this component inside the AuthProvider so it has access to auth state.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@tonnie/ui';
import { useTranslation } from '@tonnie/locale';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_MS = 60 * 1000; // Show warning 60s before timeout

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

export function InactivityGuard({ children }: { children: ReactNode }) {
  const { isSignedIn, signOut } = useAuth();
  const [, setLocation] = useLocation();
  const { t } = useTranslation();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout>>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(null);
  const isSigningOut = useRef(false);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut.current) return;
    isSigningOut.current = true;

    setShowWarning(false);
    sessionStorage.removeItem('tonnie_redirect_after_auth');

    try {
      await signOut();
    } catch {
      // Proceed even if signOut fails
    }
    setLocation('/login');
  }, [signOut, setLocation]);

  const resetTimers = useCallback(() => {
    if (!isSignedIn || isSigningOut.current) return;

    setShowWarning(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    // Set warning timer (fires WARNING_BEFORE_MS before actual timeout)
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(Math.ceil(WARNING_BEFORE_MS / 1000));

      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Set actual signout timer
    timeoutRef.current = setTimeout(() => {
      handleSignOut();
    }, INACTIVITY_TIMEOUT_MS);
  }, [isSignedIn, handleSignOut]);

  const handleStaySignedIn = useCallback(() => {
    setShowWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    resetTimers();
  }, [resetTimers]);

  // Register activity listeners
  useEffect(() => {
    if (!isSignedIn) return;

    isSigningOut.current = false;
    resetTimers();

    const onActivity = () => {
      if (!showWarning) {
        resetTimers();
      }
    };

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity);
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isSignedIn, resetTimers, showWarning]);

  return (
    <>
      {children}
      <AlertDialog open={showWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auth.sessionExpiring')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('auth.sessionExpiringDesc', { seconds: countdown })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSignOut}>{t('auth.signOut')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleStaySignedIn}>
              {t('auth.staySignedIn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
